/* istanbul ignore file */
import React, { forwardRef, useMemo } from 'react';
import {
  Button,
  NavButton,
  NavLink,
  Link as RavenLink,
} from '@express-labs/raven-ui';
import { cn } from '@express-labs/raven-tools';
import s from './Link.scss';
import parseJSONstring from '../helpers/parseJSONstring';
import { BrowserRouter as Router } from 'react-router-dom';

export interface LinkBaseProps {
  /** Display Title of a link */
  title?: string;
  /** Append additional CSS class names to the list of class names on the anchor tag element */
  className?: string;
  /** Fit link to its container */
  fitContainer?: boolean;
  /** Overide font-size value */
  fontSize?: string;
  /** Minimum width */
  minWidth?: string;
  /** Url of the CTA */
  url?: string;
  /** Query string added to end of URL */
  queryParams?: string;
  /** Hash fragment added to end of URL */
  hash?: string;
  /** Links come in three sizes */
  size?: 'large' | 'medium' | 'small';
  /** The color theme for the link. */
  theme?: 'light' | 'dark';
  /** Set to blank to open in new tab */
  target?: '_blank' | '_self';
  /** Is the link disabled? */
  isAccessiblyDisabled?: boolean;
  /** Child content and/or JSX to render inside the HTML tags */
  children?: React.ReactNode;
}

/** Props when `appearance` is 'link' */
interface LinkAppearanceProps extends LinkBaseProps {
  appearance: 'link';
}

/** Props when `appearance` is 'button' */
interface ButtonAppearanceProps extends LinkBaseProps {
  appearance: 'button';
  /** The mode for buttons, only applies to Button/NavButton components */
  mode?: 'solid' | 'ghost';
}

export type LinkProps = LinkAppearanceProps | ButtonAppearanceProps;

const isInternalLink = (url: string = '/'): boolean => {
  if (!url) return true;

  const isHttp = url.startsWith('http://');
  const isHttps = url.startsWith('https://');
  return !isHttp && !isHttps;
};

const getRelativePath = (url: string): string => {
  const { pathname, search, hash } = new URL(url, window.location.origin);
  return `${pathname}${search}${hash}`;
};

const buildFinalUrl = (
  href: string,
  urlParams: string | undefined,
  hash: string | undefined
): string => {
  let baseUrl: string | undefined;

  if (!URL.canParse(href)) {
    const { protocol, hostname } = window.location;
    baseUrl = `${protocol}//${hostname}`;
  }

  if (!URL.canParse(href, baseUrl)) {
    throw new Error('Invalid href prop in Link. Not a valid URL.');
  }

  const url = new URL(href, baseUrl);

  // Merge queryParams
  const finalQueryParams = new URLSearchParams();
  for (const [key, value] of url.searchParams.entries()) {
    finalQueryParams.set(key, value);
  }

  if (urlParams) {
    const parsedURLParam = parseJSONstring(urlParams);
    let urlQueries;

    if (typeof parsedURLParam === 'string') {
      urlQueries = parsedURLParam.split('&');
    } else {
      urlQueries = parsedURLParam;
    }

    if (urlQueries) {
      urlQueries.forEach((param: string) => {
        const [key, value] = param.split('=').map(decodeURIComponent);
        finalQueryParams.set(key, value);
      });
    }
  }

  // Set the final search string
  const queryArray: string[] = [];
  for (const [key, value] of finalQueryParams.entries()) {
    queryArray.push(`${key}=${encodeURIComponent(value)}`);
  }
  url.search = queryArray.length > 0 ? `?${queryArray.join('&')}` : '';

  // Set the hash
  url.hash = hash ? `#${encodeURIComponent(hash)}` : url.hash;

  let finalUrl = url.toString();

  // Remove trailing slash if present and if it's not the root
  if (finalUrl.endsWith('/')) {
    finalUrl = finalUrl.slice(0, -1);
  }

  return finalUrl;
};

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    appearance = 'link',
    children,
    className,
    fitContainer = false,
    fontSize = '',
    hash = '',
    isAccessiblyDisabled,
    minWidth = '',
    queryParams,
    size = 'small',
    target = '_self',
    theme: originalTheme = 'dark',
    title,
    url = '/',
    ...restProps
  }: LinkProps,
  ref
) {
  const fullUrl = buildFinalUrl(url, queryParams, hash);
  const theme = originalTheme || 'dark';

  const transformedSize = useMemo(() => {
    if (appearance === 'link') {
      return size === 'large'
        ? 'primary'
        : size === 'medium'
          ? 'secondary'
          : 'body';
    } else {
      return size === 'large' ? 'mega' : size === 'medium' ? 'large' : 'small';
    }
  }, [size, appearance]);

  const linkStyles = useMemo<React.CSSProperties>(
    () => ({
      ...(fontSize && { fontSize: fontSize }),
      ...(minWidth && { minWidth: minWidth }),
    }),
    []
  );

  const commonProps = {
    className: cn(
      isAccessiblyDisabled && s.inactive,
      fitContainer && s.fitToContainer,
      className
    ),
    style: linkStyles,
    target,
    ref: ref as React.Ref<HTMLAnchorElement>,
    ...restProps,
  };

  const ComponentToRender = useMemo(() => {
    if (isInternalLink(url)) {
      const relativePath = getRelativePath(fullUrl);
      if (appearance === 'link') {
        return (
          <Router>
            <NavLink
              theme={theme}
              size={transformedSize as 'primary' | 'secondary' | 'body'}
              to={relativePath}
              {...commonProps}
            >
              {children || title}
            </NavLink>
          </Router>
        );
      } else {
        const { mode = 'solid' } = restProps as ButtonAppearanceProps;
        return (
          <Router>
            <NavButton
              theme={theme}
              size={transformedSize as 'mega' | 'large' | 'small'}
              to={relativePath}
              mode={mode}
              {...commonProps}
            >
              {children ?? title}
            </NavButton>
          </Router>
        );
      }
    } else {
      if (appearance === 'link') {
        return (
          <RavenLink
            as="a"
            size={transformedSize as 'primary' | 'secondary' | 'body'}
            theme={theme}
            href={fullUrl}
            {...commonProps}
          >
            {children ?? title}
          </RavenLink>
        );
      } else {
        const { mode = 'solid' } = restProps as ButtonAppearanceProps;
        return (
          <Button
            href={fullUrl}
            theme={theme}
            size={transformedSize as 'mega' | 'large' | 'small'}
            as="a"
            mode={mode}
            {...commonProps}
          >
            {children ?? title}
          </Button>
        );
      }
    }
  }, [url, fullUrl, appearance, title, children, commonProps]);

  return ComponentToRender;
});

Link.displayName = 'Link';

export default Link;
